import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ValidatorService } from 'src/app/shared/services/form-control-validators.service';
import {
  DataTypes,
  MasterLookup,
  ModalPopupMessages,
  ModeIcons,
  pageType,
  popupWidth,
  validationErrorMessages,
} from 'src/app/shared/utility/constants';
import {
  CBP_ENTRY_NUMBERS_INLINE_LIMIT,
  miscellaneousFormFieldsConfig,
} from '../../mawb.config';
import { ModalService } from 'src/app/shared/services/modal.service';
import {
  CbpEntryNumbersDialogData,
  CbpEntryNumbersPopupComponent,
} from '../cbp-entry-numbers-popup/cbp-entry-numbers-popup.component';
import { SaveMawbMiscellaneous } from '../../store/mawb.action';
import { Store } from '@ngxs/store';
import { CommonService } from 'src/app/shared/services/common.service';
import { firstValueFrom, map, Observable, startWith, take } from 'rxjs';
import { IMawbMiscellaneousData } from 'src/app/features/model/mawb.model';
import { IDropdownResponse } from 'src/app/shared/models/dynamic.model';
import { CommonGetScreenService } from 'src/app/shared/services/common-get-screen';
import { UserEffectivePermissionsService } from 'src/app/features/service/user-effective-permissions.service';
import { applyRpeFieldPermissionsToForm } from 'src/app/features/service/rpe-field-permission.helpers';
import { MAWB_RPE_FIELD_REGISTRY } from '../../mawb-rpe-field.registry';
import {
  MAWB_RPE_MISCELLANEOUS_FORM_SCOPE,
  MAWB_RPE_MODULE_NAMES,
} from '../../mawb-rpe.constants';

@Component({
  selector: 'app-miscellaneous',
  templateUrl: './miscellaneous.component.html',
  styleUrl: './miscellaneous.component.scss',
})
export class MiscellaneousComponent {
  public miscInfoText = '';
  public isFrom = location.pathname.split('/')[1];
  public mawbMiscForm!: FormGroup;
  public pageMode = ModeIcons.add;
  private readonly destroyRef = inject(DestroyRef);
  public mawbMiscellaneousData$!: Observable<IMawbMiscellaneousData>;
  public setMawbMiscInfoWidth: any = {};
  public setMawbMiscInfoErrorTooltip: any = {};
  public setMawbMiscInfoTooltipEvent: any = {};
  private readonly errorMessages: any;
  public agentData!: IDropdownResponse[];
  public filteredAgentOptions!: Observable<IDropdownResponse[]>;
  public isMawbInformationScreen = false;
  /** PAC-24713: read-only entry numbers from MAWB fetch (`miscellaneous.usCBPEntryNumbers`). */
  public usCBPEntryNumbers: string[] = [];
  public readonly cbpMessages = ModalPopupMessages;
  private currentMawbId: number | null = null;
  private readonly modalService = inject(ModalService);

  constructor(
    private readonly formUtilsService: ValidatorService,
    private readonly store: Store,
    public readonly commonService: CommonService,
    private readonly getScreenService: CommonGetScreenService,
    private readonly userEffectivePermissionsService: UserEffectivePermissionsService
  ) {
    this.createForm();
    this.checkMode();
    this.mawbMiscellaneousData$ = this.store.select(
      state => state.mawbMiscellaneous.miscellaneous
    );
    this.trackCbpEntryNumbers();
  }

  ngOnInit() {
    this.dataFetch();
    this.userEffectivePermissionsService.subscribeLocationRefresh(
      this.destroyRef,
      () => this.applyRpeFieldPermissions()
    );
  }

  private applyRpeFieldPermissions(): void {
    if (!this.isMawbInformationScreen || !this.mawbMiscForm) {
      return;
    }
    applyRpeFieldPermissionsToForm(
      this.userEffectivePermissionsService,
      MAWB_RPE_MODULE_NAMES,
      this.mawbMiscForm,
      MAWB_RPE_FIELD_REGISTRY,
      this.pageMode,
      MAWB_RPE_MISCELLANEOUS_FORM_SCOPE
    );
  }

  public createForm() {
    const { form } = this.formUtilsService.generateForm(
      miscellaneousFormFieldsConfig
    , this.destroyRef);
    this.mawbMiscForm = form;
    this.loadInitialApi();
  }

  public getErrorMessage(controlName: string): string {
    return this.formUtilsService.getErrorMessage(
      this.mawbMiscForm,
      this.errorMessages,
      controlName
    );
  }

  public fetchWidth(event: MouseEvent, controlName: string) {
    this.commonService.setErrorTooltipData(
      event,
      controlName,
      this.setMawbMiscInfoWidth,
      this.setMawbMiscInfoTooltipEvent,
      this.setMawbMiscInfoErrorTooltip
    );
  }

  public checkControlHasError(controlName: string) {
    return this.commonService.checkControlError(this.mawbMiscForm, controlName);
  }

  public dispatchMiscellaneousMawbFormValueChanges() {
    const rawValue: IMawbMiscellaneousData = this.mawbMiscForm.getRawValue();
    // PAC-24713: keep the read-only CBP entry numbers in the slice so the
    // hyperlink survives edits to the other Miscellaneous fields.
    if (this.usCBPEntryNumbers.length) {
      rawValue.usCBPEntryNumbers = this.usCBPEntryNumbers;
    }
    this.store.dispatch(new SaveMawbMiscellaneous(rawValue));
    this.commonService.checkPristineAccordionData(false);
  }

  /** PAC-24713 AC3: open the read-only US CBP Entry Numbers grid. */
  public openCbpEntryNumbersPopup() {
    if (!this.currentMawbId) {
      return;
    }
    this.modalService.openPopup(
      { mawbId: this.currentMawbId } satisfies CbpEntryNumbersDialogData,
      ModalPopupMessages.usCbpEntryNumbersLabel,
      CbpEntryNumbersPopupComponent,
      popupWidth.cbpEntryNumbersPopup
    );
  }

  /** Create mode = anything that is not View/Edit (covers '' and Add). No badge in create. */
  public get isAddMode(): boolean {
    return (
      this.pageMode !== ModeIcons.view && this.pageMode !== ModeIcons.edit
    );
  }

  public get hasCbpEntryNumbers(): boolean {
    return this.usCBPEntryNumbers.length > 0;
  }

  /** Link + "Available" only in View/Edit with entries; create mode is always "Not Available". */
  public get showCbpEntryNumbersLink(): boolean {
    return !this.isAddMode && this.hasCbpEntryNumbers;
  }

  public get visibleCbpEntryNumbers(): string {
    return this.usCBPEntryNumbers
      .slice(0, CBP_ENTRY_NUMBERS_INLINE_LIMIT)
      .join(', ');
  }

  public get remainingCbpEntryNumbersCount(): number {
    return Math.max(
      this.usCBPEntryNumbers.length - CBP_ENTRY_NUMBERS_INLINE_LIMIT,
      0
    );
  }

  private trackCbpEntryNumbers() {
    this.mawbMiscellaneousData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        const entryNumbers = data?.usCBPEntryNumbers;
        this.usCBPEntryNumbers = Array.isArray(entryNumbers)
          ? entryNumbers.filter(Boolean)
          : [];
      });
    this.commonService.idData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        const mawbId = Number(data?.mawbId);
        this.currentMawbId = mawbId > 0 ? mawbId : null;
      });
  }

  public formAutocompleteValueChanges(
    controlName: string,
    apiControlName: string,
    dropdownResponse: any,
    formControlId?: string
  ) {
    this.commonService.getAutocompleteDropdownId(
      dropdownResponse,
      this.mawbMiscForm,
      controlName,
      apiControlName,
      false,
      formControlId
    );
    this.dispatchMiscellaneousMawbFormValueChanges();
  }

  public onOptionSelectedFormChanges(
    dropdownResponse: any,
    controlName: string,
    apiControlName: string
  ) {
    this.commonService.getAutocompleteDropdownBinding(
      dropdownResponse,
      this.mawbMiscForm,
      controlName,
      apiControlName,
      validationErrorMessages.name
    );
    this.commonService.checkPristineAccordionData(false);
    this.dispatchMiscellaneousMawbFormValueChanges();
  }

  private checkMode() {
    this.commonService.isCheckForMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.checkScreen();
        this.pageMode = data ?? ModeIcons.add;
        if (this.pageMode === ModeIcons.view) {
          this.mawbMiscForm.disable();
        } else {
          this.mawbMiscForm.enable();
          this.applyRpeFieldPermissions();
        }
      });
  }


  private checkScreen() {
    const location = window.location.pathname;
    const screen = this.getScreenService.getScreenFromUrl(
      this.isFrom,
      location
    );
    if (screen === pageType.mawb) {
      this.isMawbInformationScreen = true;
      this.miscInfoText = ModalPopupMessages.mawbMiscellaneousMessage;
    } else if (screen === pageType.mawbFlight) {
      this.isMawbInformationScreen = false;
      this.miscInfoText = ModalPopupMessages.mawbMiscellaneousFlightMessage;
    } else {
      this.isMawbInformationScreen = false;
      this.miscInfoText = ModalPopupMessages.loreum;
    }
  }

  private dataFetch() {
    this.commonService.savedBagData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === pageType.dataBind) {
          this.fetchOldValues();
        }
      });
  }

  private fetchOldValues() {
    this.mawbMiscellaneousData$
      .pipe(take(1),takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.mawbMiscForm.patchValue(data);
        this.applyRpeFieldPermissions();
      });
  }

  private async loadInitialApi() {
    const [
      agentData,
    ]: any = await Promise.all([
      this.getMasterLookups(MasterLookup.agentInfo),
    ]);
    this.agentData = agentData;
    this.filteredAgentOptions = this.autocompleteValueChanges(
      'clearanceAgent',
      this.agentData
    );
  }

  private async getMasterLookups(requestType: string): Promise<any[]> {
    try {
      const response = await firstValueFrom(this.commonService.getMasterLookUp(requestType));
      return response?.data ?? [];
    } catch (err) {
      console.error('Lookup fetch failed:', err);
      return [];
    }
  }

  private autocompleteValueChanges(
    controlName: string,
    dropdownData: IDropdownResponse[]
  ): Observable<IDropdownResponse[]> {
    const formControl = this.mawbMiscForm.get(controlName) as FormControl;
    return formControl.valueChanges.pipe(
      startWith(''),
      map(val => {
        const filterValue =
          typeof val === DataTypes.String ? val.toLowerCase() : '';
        return dropdownData?.filter(
          item => item.name?.toLowerCase().includes(filterValue)
        );
      })
    );
  }
}
